/**
 * Encoding/decoding of the data embedded in share links.
 *
 * Two formats are supported:
 *  - Compressed (current): '~' + URL-safe base64 of DEFLATE(utf8(JSON)). The 3D
 *    placement arrays (productPlacements / boxInstances) are extremely repetitive,
 *    so deflate shrinks the payload ~15-20x — the difference between a URL nginx
 *    accepts and a 414 Request-URI Too Large. Output stays in `~[A-Za-z0-9-_]`,
 *    fully query-string safe (no '+', '/', '%' or '=').
 *  - Legacy (still decoded): URL-safe base64 of the UTF-8 JSON — itself replacing
 *    an even older plain btoa(JSON) that threw on non-Latin1 characters (é, €, «…).
 *    Old links already shared in the wild keep working; the '~' marker (which a
 *    legacy string can never start with) disambiguates the two formats.
 */
import { deflateSync, inflateSync, strToU8, strFromU8 } from 'fflate';
import { BoxInstance, Dimensions, PackagingSolution, Product, ProductPlacement, RotationOption } from '../types/index.ts';

// Marks the compressed format. '~' is encodeURIComponent-unreserved and can never
// begin a legacy URL-safe-base64 string (those start with base64 of '{"' = 'ey').
const COMPRESSED_PREFIX = '~';

// Uint8Array -> URL-safe base64 (no padding). Chunked so a large array never hits
// "Maximum call stack size exceeded" from String.fromCharCode(...bigArray).
const bytesToBase64url = (bytes: Uint8Array): string => {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
  }
  // eslint-disable-next-line no-restricted-globals
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// URL-safe base64 (no padding) -> Uint8Array. Throws on malformed input; callers
// wrap in try/catch and return null.
const base64urlToBytes = (b64url: string): Uint8Array => {
  const base64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// Encode any JSON-serializable value into a URL-safe, compressed string.
export const encodeShareData = (data: unknown): string => {
  const bytes = strToU8(JSON.stringify(data));
  return COMPRESSED_PREFIX + bytesToBase64url(deflateSync(bytes));
};

// Decode a share parameter. Returns null on any error instead of throwing.
export const decodeShareData = (encoded: string): any | null => {
  if (!encoded) return null;

  // Current format: compressed, marked with a leading '~'.
  if (encoded[0] === COMPRESSED_PREFIX) {
    try {
      const json = strFromU8(inflateSync(base64urlToBytes(encoded.slice(1))));
      const data = JSON.parse(json);
      return typeof data === 'object' && data !== null ? data : null;
    } catch {
      return null;
    }
  }

  // Legacy format: URL-safe base64 of UTF-8 JSON (and older plain-ASCII btoa).
  // The oldest links used standard base64 with '+', which URLSearchParams
  // decodes as a space — restore those spaces to '+' before decoding.
  const base64 = encoded.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    return null;
  }

  // New links carry UTF-8 bytes; legacy links carried plain ASCII JSON, for
  // which the UTF-8 decode below is the identity.
  let json: string;
  try {
    json = decodeURIComponent(escape(binary));
  } catch {
    json = binary;
  }

  try {
    const data = JSON.parse(json);
    return typeof data === 'object' && data !== null ? data : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Sanitization of decoded share payloads.
//
// The `shared` URL parameter is the app's only untrusted input: anyone can
// craft a link. Everything the UI reads must therefore be validated, bounded
// and normalized here — otherwise a malformed or hostile payload crashes the
// shared page to a blank screen (missing dimensions), renders "NaN%", or
// freezes the tab (deflate bomb inflating to hundreds of thousands of 3D
// placements).
// ---------------------------------------------------------------------------

const MAX_SHARED_SOLUTIONS = 50;
const MAX_SHARED_PRODUCTS = 20;
const MAX_SHARED_INSTANCES = 50;
const MAX_SHARED_PLACEMENTS = 500;

const ORIENTATIONS = ['standard', 'tilted', 'length-rotated', 'width-rotated', 'flipped', 'combined-rotation'] as const;
const ROTATION_OPTIONS: RotationOption[] = ['length', 'width', 'flipped'];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const sanitizeDimensions = (d: any): Dimensions | null =>
  d &&
  isFiniteNumber(d.length) && d.length > 0 &&
  isFiniteNumber(d.width) && d.width > 0 &&
  isFiniteNumber(d.height) && d.height > 0
    ? { length: d.length, width: d.width, height: d.height }
    : null;

const sanitizePlacement = (p: any): ProductPlacement | null => {
  const dimensions = sanitizeDimensions(p?.dimensions);
  const position = p?.position;
  if (!dimensions || !position ||
      !isFiniteNumber(position.x) || !isFiniteNumber(position.y) || !isFiniteNumber(position.z)) {
    return null;
  }
  return {
    productIndex: isFiniteNumber(p.productIndex) && p.productIndex >= 0 ? Math.floor(p.productIndex) : 0,
    dimensions,
    position: { x: position.x, y: position.y, z: position.z },
  };
};

const sanitizePlacementList = (list: any): ProductPlacement[] =>
  (Array.isArray(list) ? list.slice(0, MAX_SHARED_PLACEMENTS) : [])
    .map(sanitizePlacement)
    .filter((p): p is ProductPlacement => p !== null);

const sanitizeProduct = (p: any, index: number): Product | null => {
  const dimensions = sanitizeDimensions(p?.dimensions);
  if (!dimensions) return null;
  return {
    id: typeof p.id === 'string' && p.id ? p.id : `shared_product_${index}`,
    quantity: isFiniteNumber(p.quantity) && p.quantity >= 1 ? Math.floor(p.quantity) : 1,
    dimensions,
    unit: p.unit === 'cm' ? 'cm' : 'in',
    rotationOptions: Array.isArray(p.rotationOptions)
      ? p.rotationOptions.filter((r: any): r is RotationOption => ROTATION_OPTIONS.includes(r))
      : [],
  };
};

const sanitizeSolution = (s: any, index: number): PackagingSolution | null => {
  if (!s || typeof s !== 'object') return null;
  if (!isFiniteNumber(s.productsPerBox) || s.productsPerBox <= 0) return null;
  if (!isFiniteNumber(s.totalBoxesNeeded) || s.totalBoxesNeeded < 1) return null;

  const boxInstances: BoxInstance[] | undefined = Array.isArray(s.boxInstances)
    ? s.boxInstances.slice(0, MAX_SHARED_INSTANCES).map((instance: any, i: number) => ({
        instanceIndex: isFiniteNumber(instance?.instanceIndex) ? Math.floor(instance.instanceIndex) : i,
        productPlacements: sanitizePlacementList(instance?.productPlacements),
      }))
    : undefined;

  return {
    boxId: typeof s.boxId === 'string' && s.boxId ? s.boxId : `shared_box_${index}`,
    boxName: typeof s.boxName === 'string' ? s.boxName.slice(0, 200) : 'Box',
    boxDimensions: sanitizeDimensions(s.boxDimensions) ?? undefined,
    boxUnit: s.boxUnit === 'cm' ? 'cm' : s.boxUnit === 'in' ? 'in' : undefined,
    productsPerBox: Math.floor(s.productsPerBox),
    totalBoxesNeeded: Math.floor(s.totalBoxesNeeded),
    orientation: ORIENTATIONS.includes(s.orientation) ? s.orientation : 'standard',
    spaceUtilization: isFiniteNumber(s.spaceUtilization)
      ? Math.min(100, Math.max(0, s.spaceUtilization))
      : 0,
    products: Array.isArray(s.products)
      ? (s.products.slice(0, MAX_SHARED_PRODUCTS).map(sanitizeProduct).filter(Boolean) as Product[])
      : undefined,
    totalProductsQuantity:
      isFiniteNumber(s.totalProductsQuantity) && s.totalProductsQuantity > 0
        ? Math.floor(s.totalProductsQuantity)
        : undefined,
    price:
      typeof s.price === 'string'
        ? s.price.slice(0, 100)
        : isFiniteNumber(s.price)
        ? String(s.price)
        : undefined,
    productPlacements: Array.isArray(s.productPlacements)
      ? sanitizePlacementList(s.productPlacements)
      : undefined,
    boxInstances,
  };
};

export type SanitizedSharePayload = { products: Product[]; solutions: PackagingSolution[] };

/**
 * Validates and normalizes a decoded share payload. Returns null when nothing
 * renderable can be extracted; otherwise every field the shared page reads is
 * present, finite and bounded (counts, placements, string lengths).
 */
export const sanitizeSharedPayload = (data: any): SanitizedSharePayload | null => {
  if (!data || typeof data !== 'object') return null;
  const rawProducts = Array.isArray(data.products) ? data.products : data.products ? [data.products] : [];
  const products = rawProducts
    .slice(0, MAX_SHARED_PRODUCTS)
    .map(sanitizeProduct)
    .filter((p: Product | null): p is Product => p !== null);
  const rawSolutions = Array.isArray(data.solutions) ? data.solutions : [];
  const solutions = rawSolutions
    .slice(0, MAX_SHARED_SOLUTIONS)
    .map(sanitizeSolution)
    .filter((s: PackagingSolution | null): s is PackagingSolution => s !== null);
  if (products.length === 0 || solutions.length === 0) return null;
  return { products, solutions };
};
